import { Box, Button, TextField, MenuItem, Typography, Alert, Breadcrumbs, useTheme } from "@mui/material";
import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector }  from "react-redux";
import { useState, useEffect } from "react";
import moment           from "moment";

import BadgeIcon            from "@mui/icons-material/Badge";
import ChevronRightIcon     from "@mui/icons-material/ChevronRight";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MonetizationOnIcon   from "@mui/icons-material/MonetizationOn";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CardGiftcardIcon     from "@mui/icons-material/CardGiftcard";

import RenumerationDetailForm from "./renumerationDetail";
import DeductionDetailForm    from "./deductionDetailForm";
import AllowanceDetailForm    from "./allowanceDetail";
import { useEmployeesList }   from "api/hooks/humanResource";
import { requester }          from "api/pesabackend";
import { currencyFormatter }  from "utilities/currencyFormatter";

const PayslipForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const userId             = useSelector((s) => s.authReducer.user.id);
  const business_id        = useSelector((s) => s.authReducer.business_id);
  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { employeeList }   = useEmployeesList(1, business_branch_id);

  const paySlipType = location.state.payslipType;

  const [isFailed,    setIsFailed]    = useState(false);
  const [isSuccess,   setIsSuccess]   = useState(false);
  const [isFailedError, setIsFailedError] = useState("");

  const [totalAllowance,    setTotalAllowance]    = useState(paySlipType === "allowance"    ? location.state.data.total_allowance    : 0);
  const [totalDeduction,    setTotalDeduction]    = useState(paySlipType === "renumeration" ? location.state.data.total_deduction    : 0);
  const [totalRenumeration, setTotalRenumeration] = useState(paySlipType === "renumeration" ? location.state.data.total_renumeration : 0);
  const [overallTotal,      setOverallTotal]      = useState(Number(totalRenumeration) + Number(totalAllowance) - Number(totalDeduction));

  const [renumerationDetailFormData, setRenumerationDetailFormData] = useState(paySlipType === "renumeration" ? location.state.data.renumeration : []);
  const [allowanceDetailFormData,    setAllowanceDetailFormData]    = useState(paySlipType === "allowance"    ? location.state.data.allowance    : []);
  const [deductionDetailFormData,    setDeductionDetailFormData]    = useState(paySlipType === "renumeration" ? location.state.data.deduction    : []);

  useEffect(() => {
    const t = setTimeout(() => { setIsSuccess(false); setIsFailed(false); }, 3000);
    return () => clearTimeout(t);
  }, [isSuccess, isFailed]);

  const initialValues = {
    related_employee:   location.state.id,
    total_renumeration: paySlipType === "renumeration" ? location.state.data.total_renumeration : 0,
    total_deduction:    paySlipType === "renumeration" ? location.state.data.total_deduction    : 0,
    total_allowance:    paySlipType === "allowance"    ? location.state.data.total_allowance    : 0,
    payment_date:       moment().format("YYYY-MM-DD"),
    payslip_for:        "",
    payslip_type:       paySlipType,
    renumeration_detail: paySlipType === "renumeration" ? location.state.data.renumeration : [],
    allowance_detail:    paySlipType === "allowance"    ? location.state.data.allowance    : [],
    deduction_detail:    paySlipType === "renumeration" ? location.state.data.deduction    : [],
  };

  const payslipSchema = paySlipType === "renumeration"
    ? yup.object().shape({
        related_employee:   yup.number().required("required"),
        total_renumeration: yup.number().required("required"),
        total_deduction:    yup.number().required("required"),
        payslip_for:        yup.string().required("required"),
        payment_date:       yup.date().required("required"),
        payslip_type:       yup.string(),
        renumeration_detail: yup.array().of(yup.object().shape({ related_renumeration_type: yup.number().required("required"), amount: yup.number().required("required") })),
        deduction_detail:    yup.array().of(yup.object().shape({ related_deduction_type:    yup.number().required("required"), amount: yup.number().required("required") })),
      })
    : yup.object().shape({
        related_employee: yup.number().required("required"),
        total_allowance:  yup.number().required("required"),
        payslip_for:      yup.string().required("required"),
        payment_date:     yup.date().required("required"),
        payslip_type:     yup.string(),
        allowance_detail: yup.array().of(yup.object().shape({ related_allowance_type: yup.number().required("required"), amount: yup.number().required("required") })),
      });

  const recalcTotals = (r, a, d) => {
    setOverallTotal(Number(r) + Number(a) - Number(d));
  };

  const handleTotalRenumeration = (arr) => {
    const t = arr.reduce((s, i) => s + Number(i.amount), 0);
    setTotalRenumeration(t); recalcTotals(t, totalAllowance, totalDeduction);
  };
  const handleTotalAllowance = (arr) => {
    const t = arr.reduce((s, i) => s + Number(i.amount), 0);
    setTotalAllowance(t); recalcTotals(totalRenumeration, t, totalDeduction);
  };
  const handleTotalDeduction = (arr) => {
    const t = arr.reduce((s, i) => s + Number(i.amount), 0);
    setTotalDeduction(t); recalcTotals(totalRenumeration, totalAllowance, t);
  };

  const handleRenumerationAmountChange = (e, i) => {
    const next = renumerationDetailFormData.map((r, idx) => idx === i ? { ...r, amount: Number(e.target.value) } : r);
    setRenumerationDetailFormData(next); handleTotalRenumeration(next);
  };
  const handleAllowanceAmountChange = (e, i) => {
    const next = allowanceDetailFormData.map((r, idx) => idx === i ? { ...r, amount: Number(e.target.value) } : r);
    setAllowanceDetailFormData(next); handleTotalAllowance(next);
  };
  const handleDeductionAmountChange = (e, i) => {
    const next = deductionDetailFormData.map((r, idx) => idx === i ? { ...r, amount: Number(e.target.value) } : r);
    setDeductionDetailFormData(next); handleTotalDeduction(next);
  };

  const savePayslip = async (values, onSubmitProps) => {
    const res = await requester.post("/human_resource/payslip", JSON.stringify({
      ...values,
      related_business: business_id,
      related_business_branch: business_branch_id,
      added_by: userId,
    }));
    if (res.data.status === 201) {
      setIsSuccess(true);
      setTimeout(() => { onSubmitProps.resetForm(); navigate("/humanResource"); }, 1500);
    }
    if (res.data.status === 400) { setIsFailed(true); setIsFailedError(res.data.errors); }
  };

  const fieldSx = {
    "& .MuiInputBase-root": { fontSize: "13px", borderRadius: "8px", backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#fafafa" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "13px" },
  };

  /* ── Section card ── */
  const SectionCard = ({ title, icon, iconColor, children }) => (
    <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", overflow: "hidden" }}>
      <Box sx={{ px: "1.25rem", py: "0.875rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)", display: "flex", alignItems: "center", gap: "8px" }}>
        <Box sx={{ color: iconColor, opacity: 0.8 }}>{icon}</Box>
        <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>{title}</Typography>
      </Box>
      <Box sx={{ p: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>{children}</Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {isSuccess && <Alert variant="filled" severity="success" sx={{ borderRadius: "8px" }}>Payslip recorded successfully!</Alert>}
      {isFailed  && <Alert variant="filled" severity="error"   sx={{ borderRadius: "8px" }}>{isFailedError || "Operation was not successful!"}</Alert>}

      {/* Breadcrumb + header */}
      <Box>
        <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: "14px" }} />} sx={{ mb: "0.5rem" }}>
          <Typography onClick={() => navigate("/humanResource")} sx={{ fontSize: "12px", color: accent, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { textDecoration: "underline" } }}>
            <BadgeIcon sx={{ fontSize: "14px" }} /> Human Resource
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec }}>Create Payslip</Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <MonetizationOnIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              {paySlipType === "renumeration" ? "Salary & Deductions Payslip" : "Allowance Payslip"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>Complete the form and save the payslip</Typography>
          </Box>
        </Box>
      </Box>

      <Formik onSubmit={savePayslip} initialValues={initialValues} validationSchema={payslipSchema} validateOnChange={false} validateOnBlur={false}>
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, resetForm, setFieldValue, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

              {/* Meta card */}
              <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", overflow: "hidden" }}>
                <Box sx={{ px: "1.25rem", py: "0.875rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>Payslip Details</Typography>
                </Box>
                <Box sx={{ p: "1.25rem", display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                  {/* Payment date */}
                  <Box sx={{ flex: "1 1 200px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "6px" }}>Payment Date</Typography>
                    <TextField fullWidth type="date" size="small" name="payment_date" value={values.payment_date}
                      onBlur={handleBlur} onChange={handleChange}
                      error={Boolean(touched.payment_date) && Boolean(errors.payment_date)}
                      helperText={touched.payment_date && errors.payment_date}
                      sx={fieldSx} />
                  </Box>
                  {/* Employee (read-only) */}
                  <Box sx={{ flex: "1 1 200px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "6px" }}>Employee</Typography>
                    <TextField fullWidth select disabled size="small" name="related_employee" value={values.related_employee}
                      onBlur={handleBlur} onChange={handleChange}
                      error={Boolean(touched.related_employee) && Boolean(errors.related_employee)}
                      helperText={touched.related_employee && errors.related_employee}
                      sx={fieldSx}>
                      {employeeList.map((r) => <MenuItem key={r.id} value={r.id}>{r.first_name} {r.last_name}</MenuItem>)}
                    </TextField>
                  </Box>
                  {/* Payment for */}
                  <Box sx={{ flex: "2 1 400px" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mb: "6px" }}>Payment For</Typography>
                    <TextField fullWidth multiline rows={2} size="small" name="payslip_for" value={values.payslip_for}
                      onBlur={handleBlur} onChange={handleChange} placeholder="Describe what this payslip is for…"
                      error={Boolean(touched.payslip_for) && Boolean(errors.payslip_for)}
                      helperText={touched.payslip_for && errors.payslip_for}
                      sx={fieldSx} />
                  </Box>
                </Box>
              </Box>

              {/* Entry sections */}
              <Box sx={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", alignItems: "flex-start" }}>

                {paySlipType === "renumeration" && (
                  <>
                    <SectionCard title="Renumeration" icon={<MonetizationOnIcon sx={{ fontSize: "16px" }} />} iconColor={accent}>
                      <FieldArray name="renumeration_detail" render={(arrayHelpers) => (
                        <>
                          {values.renumeration_detail.map((row, index) => (
                            <RenumerationDetailForm
                              key={index} index={index} data={row}
                              handleRemoveProductDetailRow={(i) => { arrayHelpers.remove(i); const next = [...renumerationDetailFormData]; next.splice(i, 1); setRenumerationDetailFormData(next); handleTotalRenumeration(next); }}
                              handleItemAmountChange={handleRenumerationAmountChange}
                              handleChange={handleChange} errors={arrayHelpers.form.errors}
                              touched={arrayHelpers.form.touched} name={`renumeration_detail[${index}]`}
                              handleBlur={handleBlur} setFieldValue={setFieldValue} businessBranchId={business_branch_id}
                            />
                          ))}
                          <Box onClick={() => { arrayHelpers.push({ related_renumeration_type: "", amount: 0 }); setRenumerationDetailFormData([...renumerationDetailFormData, { related_renumeration_type: "", amount: 0 }]); }}
                            sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", p: "0.6rem", borderRadius: "8px", border: `1px dashed rgba(29,158,117,0.4)`, cursor: "pointer", color: accent, fontSize: "13px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.05)" } }}>
                            <AddCircleOutlineIcon sx={{ fontSize: "15px" }} /> Add Renumeration
                          </Box>
                        </>
                      )} />
                    </SectionCard>

                    <SectionCard title="Deductions" icon={<RemoveCircleOutlineIcon sx={{ fontSize: "16px" }} />} iconColor="#e24b4a">
                      <FieldArray name="deduction_detail" render={(arrayHelpers) => (
                        <>
                          {values.deduction_detail.map((row, index) => (
                            <DeductionDetailForm
                              key={index} index={index} data={row}
                              handleRemoveProductDetailRow={(i) => { arrayHelpers.remove(i); const next = [...deductionDetailFormData]; next.splice(i, 1); setDeductionDetailFormData(next); handleTotalDeduction(next); }}
                              handleItemAmountChange={handleDeductionAmountChange}
                              handleChange={handleChange} errors={arrayHelpers.form.errors}
                              touched={arrayHelpers.form.touched} name={`deduction_detail[${index}]`}
                              handleBlur={handleBlur} setFieldValue={setFieldValue} businessBranchId={business_branch_id}
                            />
                          ))}
                          <Box onClick={() => { arrayHelpers.push({ related_deduction_type: "", amount: 0 }); setDeductionDetailFormData([...deductionDetailFormData, { related_deduction_type: "", amount: 0 }]); }}
                            sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", p: "0.6rem", borderRadius: "8px", border: "1px dashed rgba(226,75,74,0.4)", cursor: "pointer", color: "#e24b4a", fontSize: "13px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.05)" } }}>
                            <AddCircleOutlineIcon sx={{ fontSize: "15px" }} /> Add Deduction
                          </Box>
                        </>
                      )} />
                    </SectionCard>
                  </>
                )}

                {paySlipType === "allowance" && (
                  <SectionCard title="Allowances" icon={<CardGiftcardIcon sx={{ fontSize: "16px" }} />} iconColor="#BA7517">
                    <FieldArray name="allowance_detail" render={(arrayHelpers) => (
                      <>
                        {values.allowance_detail.map((row, index) => (
                          <AllowanceDetailForm
                            key={index} index={index} data={row}
                            handleRemoveProductDetailRow={(i) => { arrayHelpers.remove(i); const next = [...allowanceDetailFormData]; next.splice(i, 1); setAllowanceDetailFormData(next); handleTotalAllowance(next); }}
                            handleItemAmountChange={handleAllowanceAmountChange}
                            handleChange={handleChange} errors={arrayHelpers.form.errors}
                            touched={arrayHelpers.form.touched} name={`allowance_detail[${index}]`}
                            handleBlur={handleBlur} setFieldValue={setFieldValue} businessBranchId={business_branch_id}
                          />
                        ))}
                        <Box onClick={() => { arrayHelpers.push({ related_allowance_type: "", amount: 0 }); setAllowanceDetailFormData([...allowanceDetailFormData, { related_allowance_type: "", amount: 0 }]); }}
                          sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", p: "0.6rem", borderRadius: "8px", border: "1px dashed rgba(186,117,23,0.4)", cursor: "pointer", color: "#BA7517", fontSize: "13px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.05)" } }}>
                          <AddCircleOutlineIcon sx={{ fontSize: "15px" }} /> Add Allowance
                        </Box>
                      </>
                    )} />
                  </SectionCard>
                )}
              </Box>

              {/* Totals summary */}
              <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", overflow: "hidden", ml: "auto", minWidth: "320px" }}>
                <Box sx={{ px: "1.25rem", py: "0.875rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>Summary</Typography>
                </Box>
                <Box>
                  {paySlipType === "renumeration" ? (
                    <>
                      {[
                        { label: "Total Renumeration", value: currencyFormatter(totalRenumeration), formKey: "total_renumeration", stateVal: totalRenumeration },
                        { label: "Total Deductions",   value: currencyFormatter(totalDeduction),    formKey: "total_deduction",    stateVal: totalDeduction    },
                        { label: "Net Pay",            value: currencyFormatter(overallTotal),      formKey: "overall_total",      stateVal: overallTotal,      highlight: true },
                      ].map(({ label, value, formKey, stateVal, highlight }) => {
                        // keep Formik in sync
                        values[formKey] = stateVal;
                        return (
                          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "1.25rem", py: "0.7rem", borderBottom: `1px solid ${border}`, backgroundColor: highlight ? (isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)") : "transparent" }}>
                            <Typography sx={{ fontSize: "12px", color: textSec }}>{label}</Typography>
                            <Typography sx={{ fontSize: highlight ? "17px" : "13px", fontWeight: highlight ? 600 : 500, color: highlight ? accent : textPri }}>{value}</Typography>
                          </Box>
                        );
                      })}
                    </>
                  ) : (
                    (() => { values.total_allowance = totalAllowance; return (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "1.25rem", py: "0.7rem", backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)" }}>
                        <Typography sx={{ fontSize: "12px", color: textSec }}>Total Allowances</Typography>
                        <Typography sx={{ fontSize: "17px", fontWeight: 600, color: accent }}>{currencyFormatter(totalAllowance)}</Typography>
                      </Box>
                    ); })()
                  )}
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: "flex", gap: "0.75rem" }}>
                <Button type="submit"
 disabled={isSubmitting}
                  sx={{ fontSize: "13px", fontWeight: 500, px: "1.5rem", py: "0.5rem", borderRadius: "8px", textTransform: "none", backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: "#0F6E56" } }}>
                  Save Payslip
                </Button>
                <Button onClick={() => { resetForm(); navigate("/humanResource"); }}
                  sx={{ fontSize: "13px", fontWeight: 500, px: "1.5rem", py: "0.5rem", borderRadius: "8px", textTransform: "none", border: `1px solid rgba(226,75,74,0.3)`, color: "#e24b4a", "&:hover": { backgroundColor: "rgba(226,75,74,0.06)" } }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PayslipForm;